import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { client } from '../lib/client';
import { desktop } from '../lib/desktop';
import { errorMessage } from '../lib/errors';

/** Whether Jira is set up on the server and the person's own connection; `jira.connection` refreshes it. */
export function useJira() {
  return useQuery({ queryKey: ['jira'], queryFn: () => client.api.jira(), staleTime: 5 * 60_000 });
}

/** Settings › Bağlantılar: "Jira'yı bağla" (docs/plans/2026-11-04-jira-design.md §1). */
export function JiraSettings() {
  const queries = useQueryClient();
  const jira = useJira();
  const [waiting, setWaiting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const connection = jira.data?.connection ?? null;

  async function connect() {
    setError(null);
    // Opened during the click, or browsers block it; the desktop app sends it to the default browser instead.
    const tab = desktop ? null : window.open('', '_blank');
    try {
      const { url } = await client.api.connectJira();
      if (tab) {
        tab.opener = null;
        tab.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener');
      }
      setWaiting(true);
    } catch (e) {
      tab?.close();
      setError(errorMessage(e));
    }
  }

  async function disconnect() {
    setError(null);
    setBusy(true);
    try {
      await client.api.disconnectJira();
      await queries.invalidateQueries({ queryKey: ['jira'] });
      setWaiting(false);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (jira.isPending) return <p className="m-0 text-sm text-fg-3">Yükleniyor…</p>;
  if (!jira.data) return <p className="m-0 text-sm text-[#EE9191]">{errorMessage(jira.error)}</p>;

  return (
    <section aria-labelledby="jira-title" className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <span aria-hidden="true" className="grid size-9 flex-none place-items-center rounded-sm bg-[#1868DB] font-heading text-base font-bold text-white">
          J
        </span>
        <div className="min-w-0 flex-1">
          <h3 id="jira-title" className="m-0 text-sm font-semibold">
            Jira
          </h3>
          <p className="m-0 truncate text-xs text-fg-3">{new URL(jira.data.site).host}</p>
        </div>
        {connection && <span className="rounded-xs bg-green-400/15 px-2 py-1 text-xs font-semibold text-green-400">Bağlı</span>}
      </div>

      {connection ? (
        <>
          <div className="rounded-md border border-line bg-surface-2 px-3 py-2.5 text-sm">
            <div className="truncate font-semibold">{connection.name}</div>
            {connection.email && <div className="truncate text-xs text-fg-3">{connection.email}</div>}
          </div>
          <p className="m-0 text-[13px] leading-normal text-fg-3">NovaChat’ten Jira’da yaptığın işlemler (iş açma, durum değiştirme, atama, yorum) bu hesabın adına görünür.</p>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={busy}
              onClick={() => void disconnect()}
              className="nc-press h-9 rounded-sm border border-line-strong bg-surface-1 px-3 text-sm font-semibold text-[#EE9191] disabled:opacity-60"
            >
              Bağlantıyı kes
            </button>
            <a href="https://id.atlassian.com/manage-profile/apps" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[13px] text-fg-2 hover:text-fg-1">
              Atlassian’daki izinler <ExternalLink size={13} aria-hidden="true" />
            </a>
          </div>
        </>
      ) : (
        <>
          <p className="m-0 text-[13px] leading-normal text-fg-3">
            Jira hesabını bağlayınca NovaChat’ten iş açabilir, durum değiştirebilir, atama ve yorum yapabilirsin. İşlemler Jira’da senin adınla görünür.
          </p>
          <button type="button" onClick={() => void connect()} className="nc-press h-9 self-start rounded-sm border-0 bg-brand-500 px-4 text-sm font-bold text-white">
            Jira’yı bağla
          </button>
          {waiting && (
            <p role="status" className="m-0 text-[13px] leading-normal text-fg-2">
              Atlassian’ın onay sayfası {desktop ? 'tarayıcında' : 'yeni sekmede'} açıldı. Orada ve ardından NovaChat sayfasında onayladığında burası kendiliğinden güncellenir.
            </p>
          )}
        </>
      )}
      {error && (
        <p role="alert" className="m-0 text-[13.5px] text-[#EE9191]">
          {error}
        </p>
      )}
    </section>
  );
}
